import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Play, CheckCircle, Clock, Award, BarChart3, AlertCircle } from 'lucide-react';
import { api } from '../../api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

interface QuizSettings {
  timeLimit: number;
  maxAttempts: number;
  passingScore: number;
  showResults: boolean;
  showCorrectAnswers: boolean;
  shuffleQuestions: boolean;
  shuffleOptions: boolean;
  availableFrom: Date | null;
  availableUntil: Date | null;
}

interface QuizQuestion {
  _id: string;
  questionText: string;
  questionType: 'qcm' | 'true_false' | 'text_input' | 'multiple_choice';
  options?: string[];
  correctAnswer?: any; // Should not be sent to frontend for students
  points: number;
  explanation?: string;
}

interface Quiz {
  _id: string;
  title: string;
  description: string;
  questions: QuizQuestion[];
  assignedTo: { _id: string; title: string };
  assignedToType: 'Course' | 'Section' | 'Module';
  professor: { _id: string; name: string };
  settings: QuizSettings;
  isPublished: boolean;
  totalPoints: number;
  difficulty?: string;
  userAttempts?: number; // Number of attempts made by the current user
  userBestScore?: number; // Best score of the current user
  lastAttemptDate?: string; // Date of the last attempt
  lastAttemptTime?: number; // Time spent on last attempt
  remainingAttempts?: number; // Remaining attempts for the user
}

const StudentQuizzes: React.FC = () => {
  const [selectedQuiz, setSelectedQuiz] = useState<Quiz | null>(null);
  const [activeTab, setActiveTab] = useState<'available' | 'completed'>('available');
  const [availableQuizzes, setAvailableQuizzes] = useState<Quiz[]>([]);
  const [completedQuizzes, setCompletedQuizzes] = useState<Quiz[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const getDifficultyColor = (difficulty?: string) => {
    switch (difficulty) {
      case 'facile': return 'bg-brand-100 text-brand-800';
      case 'moyen': return 'bg-brand-100 text-brand-800';
      case 'difficile': return 'bg-brand-100 text-brand-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getScoreColor = (score: number, totalPoints: number) => {
    if (!totalPoints) return 'text-gray-500';
    if (score >= totalPoints * 0.8) return 'text-brand-600';
    if (score >= totalPoints * 0.5) return 'text-brand-600';
    return 'text-brand-600';
  };

  useEffect(() => {
    const fetchQuizzes = async () => {
      try {
        setLoading(true);
        const response = await api.get('/student/quizzes');
        const allQuizzes: Quiz[] = response.data.quizzes;

        const now = new Date();

        const available = allQuizzes.filter(quiz => {
          const isAvailableByDate = (!quiz.settings.availableFrom || new Date(quiz.settings.availableFrom) <= now) &&
                                  (!quiz.settings.availableUntil || new Date(quiz.settings.availableUntil) >= now);
          const hasAttemptsLeft = quiz.settings.maxAttempts === 0 || (quiz.remainingAttempts !== undefined && quiz.remainingAttempts > 0);
          const isNotCompleted = quiz.userAttempts === 0 || (quiz.userBestScore == null || quiz.userBestScore < quiz.settings.passingScore);
          
          return isAvailableByDate && hasAttemptsLeft && isNotCompleted;
        });

        const completed = allQuizzes.filter(quiz => {
          const hasAttempts = quiz.userAttempts && quiz.userAttempts > 0;
          const isPassed = quiz.userBestScore != null && quiz.userBestScore >= quiz.settings.passingScore;
          const noAttemptsLeft = quiz.settings.maxAttempts !== 0 && (quiz.remainingAttempts !== undefined && quiz.remainingAttempts <= 0);

          return hasAttempts && (isPassed || noAttemptsLeft);
        });

        setAvailableQuizzes(available);
        setCompletedQuizzes(completed);
      } catch (error: any) {
        console.error('Error fetching quizzes:', error);
        if (error.response?.status === 403) {
          toast.error("Vous n'êtes pas inscrit à ce cours.");
        } else {
          toast.error('Erreur lors du chargement des quiz.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchQuizzes();
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-brand-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Quiz & Tests</h1>
          <div className="text-sm text-gray-600">
            {completedQuizzes.length} quiz terminés
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('available')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'available'
                  ? 'border-brand-500 text-brand-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Quiz disponibles ({availableQuizzes.length})
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'completed'
                  ? 'border-brand-500 text-brand-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Quiz terminés ({completedQuizzes.length})
            </button>
          </nav>
        </div>

        {/* Available Quizzes */}
        {activeTab === 'available' && (
          <div className="space-y-4">
            {availableQuizzes.length > 0 ? (
              availableQuizzes.map((quiz) => (
                <div key={quiz._id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{quiz.title}</h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getDifficultyColor(quiz.difficulty)}`}>
                          {quiz.difficulty}
                        </span>
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-2">{quiz.assignedTo.title}</p>
                      <p className="text-gray-700 mb-4">{quiz.description}</p>

                      <div className="flex items-center space-x-6 text-sm text-gray-500">
                        <div className="flex items-center">
                          <BarChart3 className="h-4 w-4 mr-1" />
                          {quiz.questions.length} questions
                        </div>
                        {quiz.settings.timeLimit > 0 && (
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 mr-1" />
                            {quiz.settings.timeLimit} minutes
                          </div>
                        )}
                        {quiz.settings.maxAttempts > 0 && (
                          <div className="flex items-center">
                            <Play className="h-4 w-4 mr-1" />
                            {quiz.remainingAttempts} / {quiz.settings.maxAttempts} tentatives
                          </div>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={() => setSelectedQuiz(quiz)}
                      className="ml-4 px-6 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors flex items-center"
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Commencer
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Award className="h-12 w-12 mx-auto mb-3" />
                <p>Aucun quiz disponible pour le moment.</p>
              </div>
            )}
          </div>
        )}

        {/* Completed Quizzes */}
        {activeTab === 'completed' && (
          <div className="space-y-4">
            {completedQuizzes.length > 0 ? (
              completedQuizzes.map((quiz) => (
                <div key={quiz._id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{quiz.title}</h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getDifficultyColor(quiz.difficulty)}`}>
                          {quiz.difficulty}
                        </span>
                        {quiz.userBestScore !== undefined && quiz.userBestScore >= quiz.settings.passingScore ? (
                          <CheckCircle className="h-5 w-5 text-brand-600" />
                        ) : (
                          <AlertCircle className="h-5 w-5 text-brand-600" />
                        )}
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-2">{quiz.assignedTo.title}</p>
                      <p className="text-sm text-gray-500 mb-4">
                        Terminé le {quiz.lastAttemptDate ? new Date(quiz.lastAttemptDate).toLocaleDateString('fr-FR') : 'N/A'} à {quiz.lastAttemptDate ? new Date(quiz.lastAttemptDate).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                      </p>

                      <div className="flex items-center space-x-6 text-sm text-gray-500">
                        {quiz.lastAttemptTime !== undefined && quiz.lastAttemptTime > 0 && (
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 mr-1" />
                            {quiz.lastAttemptTime} minutes
                          </div>
                        )}
                        {quiz.userAttempts !== undefined && quiz.userAttempts > 0 && (
                          <div className="flex items-center">
                            <Play className="h-4 w-4 mr-1" />
                            Tentative {quiz.userAttempts}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="ml-4 text-right">
                      {quiz.userBestScore !== undefined && quiz.totalPoints !== undefined && (
                        <div className="flex items-center space-x-2 mb-2">
                          <Award className="h-5 w-5 text-brand-500" />
                          <span className={`text-2xl font-bold ${getScoreColor(quiz.userBestScore, quiz.totalPoints)}`}>
                            {quiz.userBestScore}/{quiz.totalPoints}
                          </span>
                        </div>
                      )}
                      {quiz.userBestScore !== undefined && quiz.totalPoints !== undefined && (
                        <p className="text-sm text-gray-500">
                          {Math.round((quiz.userBestScore / quiz.totalPoints) * 100)}% de réussite
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Award className="h-12 w-12 mx-auto mb-3" />
                <p>Aucun quiz terminé pour le moment.</p>
              </div>
            )}
          </div>
        )}

        {/* Quiz Start Modal */}
        {selectedQuiz && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  {selectedQuiz.title}
                </h2>
                <p className="text-gray-600">{selectedQuiz.description}</p>
              </div>

              <div className="p-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Questions:</span>
                    <span className="font-medium">{selectedQuiz.questions.length}</span>
                  </div>
                  {selectedQuiz.settings.timeLimit > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Temps limite:</span>
                      <span className="font-medium">{selectedQuiz.settings.timeLimit} minutes</span>
                    </div>
                  )}
                  {selectedQuiz.settings.maxAttempts > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Tentatives restantes:</span>
                      <span className="font-medium">{selectedQuiz.remainingAttempts}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Difficulté:</span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getDifficultyColor(selectedQuiz.difficulty)}`}>
                      {selectedQuiz.difficulty}
                    </span>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-brand-50 border border-brand-200 rounded-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-brand-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-brand-800">
                        Instructions importantes
                      </h3>
                      <div className="mt-2 text-sm text-brand-700">
                        <ul className="list-disc list-inside space-y-1">
                          <li>Lisez attentivement chaque question</li>
                          <li>Vous ne pouvez pas revenir en arrière</li>
                          <li>Le quiz se termine automatiquement à la fin du temps</li>
                          <li>Vos réponses sont sauvegardées automatiquement</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-gray-50 border-t border-gray-200 flex justify-between">
                <button
                  onClick={() => setSelectedQuiz(null)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={async () => {
                    try {
                      const response = await api.post(`/student/quizzes/${selectedQuiz._id}/start`);
                      toast.success(response.data.message);
                      navigate(`/student/quiz/${selectedQuiz._id}/attempt/${response.data.attemptId}`);
                    } catch (error: any) {
                      console.error('Error starting quiz:', error);
                      if (error.response?.status === 403) {
                        toast.error("Vous n'êtes pas inscrit à ce cours.");
                      } else {
                        toast.error('Erreur lors du démarrage du quiz.');
                      }
                    }
                    setSelectedQuiz(null);
                  }}
                  className="px-6 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
                >
                  Commencer le quiz
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StudentQuizzes;